import React from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LANGUAGES } from "@/i18n/translations";

const LANGUAGE_NAMES = {
  pt: "Português",
  en: "English",
  fr: "Français",
};

/**
 * Global, persistent language selector dropdown.
 * Backed by react-i18next; the choice is persisted to localStorage by the
 * LanguageDetector cache configured in i18n/config.js.
 */
export const LanguageSelector = ({ className = "" }) => {
  const { i18n } = useTranslation();
  const current = i18n.resolvedLanguage || i18n.language || "en";

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
  };

  const currentLabel =
    (LANGUAGES.find((l) => l.code === current) || { label: current.toUpperCase() }).label;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`flex items-center gap-1.5 rounded-full glass border border-white/15 px-3 py-1.5 text-xs font-semibold text-slate-100 hover:bg-white/10 transition-colors ${className}`}
          data-testid="language-selector-trigger"
        >
          <Globe className="h-3.5 w-3.5 text-venka-green-light" />
          <span>{currentLabel}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-[160px] glass-panel border-white/15 text-slate-100"
        data-testid="language-selector-menu"
      >
        {LANGUAGES.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onClick={() => changeLanguage(l.code)}
            className="flex items-center justify-between gap-3 cursor-pointer focus:bg-white/10 focus:text-white"
            data-testid={`language-option-${l.code}`}
          >
            <span className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-venka-green-light w-5">{l.label}</span>
              <span>{LANGUAGE_NAMES[l.code] || l.label}</span>
            </span>
            {current === l.code && <Check className="h-4 w-4 text-venka-green-light" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
